import { resolvePlace, suggestCities, type CitySuggestion } from "@/features/geocoders";
import { AutoComplete, Segmented, Select, Tooltip } from "antd";
import { debounce } from "lodash";
import { useMemo, useState, type ReactNode } from "react";
import { coordName, parseLatLon } from "../format";
import { hasVT, hazardGroups, metricOf, metricRows, pickHazard, pickValueType, type Selection } from "../model";
import type { Point, Summary, ValueType } from "../types";
import { Field } from "./parts";
interface Props {
  summary: Summary;
  selection: Selection;
  onChange: (change: Partial<Selection> | ((s: Selection) => Selection)) => void;
  /** Adds a location; the dashboard says so when the list is full. */
  onAdd: (point: Point) => void;
  /** Why the last location could not be added, or "". */
  message: string;
}

/** Hazard, its metric, raw value or change, and the location search: the dashboard's top bar. */
export const Controls = ({
  summary,
  selection,
  onChange,
  onAdd,
  message
}: Props) => {
  const m = metricOf(summary, selection.metric);
  const rows = metricRows(summary, selection.hazard);
  const def = summary.hazardDefaults[selection.hazard];
  const valueTypes: {
    value: ValueType;
    label: ReactNode;
    disabled: boolean;
  }[] = [{
    value: "value",
    label: <Tooltip title={hasVT(m, "value") ? "The metric itself" : "Published as a change from the baseline only"}>
          Raw value
        </Tooltip>,
    disabled: !hasVT(m, "value")
  }, {
    value: "diff",
    label: "Change from baseline",
    disabled: !hasVT(m, "diff")
  }];
  return <div className="flex flex-wrap items-end gap-x-[18px] gap-y-2.5 rounded-lg border border-solid border-gray-200 bg-surface px-[18px] py-3" data-sentry-component="Controls" data-sentry-source-file="Controls.tsx">
      <Field label="Hazard" className="w-full sm:w-auto" data-sentry-element="Field" data-sentry-source-file="Controls.tsx">
        <Select className="w-full sm:min-w-[260px]" value={selection.hazard} popupMatchSelectWidth={false} options={hazardGroups(summary).map(g => ({
        label: g.label,
        title: g.label,
        options: g.hazards.map(h => ({
          value: h,
          label: h
        }))
      }))} onChange={(hazard: string) => onChange(s => pickHazard(summary, s, hazard))} aria-label="Hazard" data-sentry-element="Select" data-sentry-source-file="Controls.tsx" />
      </Field>
      <Field label="Metric" className="w-full sm:w-auto" data-sentry-element="Field" data-sentry-source-file="Controls.tsx">
        <Select className="w-full sm:min-w-[260px]" value={selection.metric} disabled={rows.length < 2} popupMatchSelectWidth={false} options={rows.map(r => ({
        value: r.metric_id,
        label: metricOf(summary, r.metric_id).plain_label + (r.metric_id === def ? " (default)" : "")
      }))} onChange={(metric: string) => onChange({
        metric
      })} aria-label="Metric" data-sentry-element="Select" data-sentry-source-file="Controls.tsx" />
      </Field>
      <Field label="Show" data-sentry-element="Field" data-sentry-source-file="Controls.tsx">
        <Segmented value={selection.vt} options={valueTypes} onChange={vt => onChange(s => pickValueType(summary, s, vt as ValueType))} data-sentry-element="Segmented" data-sentry-source-file="Controls.tsx" />
      </Field>
      <LocationSearch onAdd={onAdd} message={message} data-sentry-element="LocationSearch" data-sentry-source-file="Controls.tsx" />
    </div>;
};

/** Town or city search through Google, or typed coordinates. */
export const LocationSearch = ({
  onAdd,
  message
}: Pick<Props, "onAdd" | "message">) => {
  const [input, setInput] = useState("");
  const [cities, setCities] = useState<CitySuggestion[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const search = useMemo(() => debounce(async (text: string) => {
    setFailed(false);
    if (text.trim().length < 2 || parseLatLon(text)) return setCities([]);
    try {
      setCities(await suggestCities(text));
    } catch {
      setCities([]);
      setFailed(true);
    }
  }, 300), []);
  const typed = parseLatLon(input);
  const options = typed ? [{
    value: "coordinates",
    label: `${coordName(typed.lat, typed.lon)} · coordinates`
  }] : cities.map(c => ({
    value: c.placeId,
    label: c.name
  }));
  const pick = async (value: string) => {
    setInput("");
    setCities([]);
    setNotFound(false);
    setFailed(false);
    if (value === "coordinates" && typed) {
      onAdd({
        name: coordName(typed.lat, typed.lon),
        lat: typed.lat,
        lon: typed.lon
      });
      return;
    }
    const city = cities.find(c => c.placeId === value);
    let place;
    try {
      place = city && (await resolvePlace(city.placeId, city.name));
    } catch {
      return setFailed(true);
    }
    if (place) onAdd({
      name: place.name,
      lat: place.lat,
      lon: place.lon,
      placeId: place.placeId,
      resolvedAt: Date.now()
    });else setNotFound(true);
  };
  return <Field label="Add a location" className="w-full sm:ml-auto sm:w-[328px]" data-sentry-element="Field" data-sentry-component="LocationSearch" data-sentry-source-file="Controls.tsx">
      <AutoComplete value={input} options={options} onSearch={text => {
      setInput(text);
      search(text);
    }} onSelect={pick} placeholder="Town or city, or lat, lon" aria-label="Search a town or city, or type lat, lon" data-sentry-element="AutoComplete" data-sentry-source-file="Controls.tsx" />
      {(message || failed || notFound) && <span className="text-xs text-red-600">
          {message || (failed ? "Google's place search did not answer. Try again, or type lat, lon." : "That place could not be found. Try another spelling, or type lat, lon.")}
        </span>}
    </Field>;
};